import { Info } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import type {
  LiveDecisionSource,
  LiveRuleDiff,
  LiveRuleRef,
  LiveSeonReview,
  LiveSeonSide,
} from "@/lib/api"
import { EMPTY, formatDateFull, formatInt, formatTime } from "@/lib/format"
import { cn } from "@/lib/utils"

/**
 * Общие куски экрана «Живые: мы и SEON» и блока SEON в карточке двойника
 * (AMEND-59 шаг 2). Одни и те же слова и цвета в обоих местах: аналитик
 * переходит из ленты в карточку и обратно, и разные подписи одного факта
 * заставили бы гадать, то же ли это самое.
 */

export type DecisionTone = "approve" | "review" | "decline" | "neutral"

export function decisionTone(state: string | null | undefined): DecisionTone {
  switch ((state ?? "").toUpperCase()) {
    case "APPROVE":
      return "approve"
    case "REVIEW":
      return "review"
    case "DECLINE":
      return "decline"
    default:
      return "neutral"
  }
}

export const TONE_TEXT: Record<DecisionTone, string> = {
  approve: "text-approve",
  review: "text-review",
  decline: "text-decline",
  neutral: "text-fg",
}

export function DecisionBadge({ state }: { state: string | null | undefined }) {
  if (!state) return <span className="text-xs text-fg-subtle">{EMPTY}</span>
  return <Badge tone={decisionTone(state)}>{state}</Badge>
}

/**
 * Откуда решение SEON (AMEND-59 шаг 3б). Платёжка просит у SEON ответ без
 * поля `state`, и решение выводится из балла по тем же порогам, что у
 * двойника, — это и есть их решение, а не догадка: пометка нейтральная.
 */
export const DECISION_SOURCE_LABEL: Record<LiveDecisionSource, string> = {
  review: "ручная",
  state: "по ответу",
  score: "по баллу",
}

const DECISION_SOURCE_HINT: Record<LiveDecisionSource, string> = {
  review: "статус ручной проверки в SEON перекрывает автоматическое решение",
  state: "поле state в ответе SEON",
  score: "по порогам балла — тем же, что у двойника",
}

/** Решение SEON с пометкой, откуда оно. */
export function SeonDecision({ seon }: { seon: LiveSeonSide }) {
  const source = seon.decision_source
  return (
    <span className="inline-flex items-center gap-1.5">
      <DecisionBadge state={seon.decision?.toUpperCase()} />
      {source && seon.decision && (
        <span className="text-xs text-fg-subtle" title={DECISION_SOURCE_HINT[source]}>
          {DECISION_SOURCE_LABEL[source]}
        </span>
      )}
    </span>
  )
}

/** Чем склеена пара: приёмник ставит её по номеру заказа или по полям. */
export const MATCH_LABEL: Record<string, string> = {
  transaction_id: "по номеру транзакции",
  order_memo: "по номеру заказа",
  attributes: "по IP / email / BIN и времени",
  ambiguous: "неоднозначно: две строки на равном расстоянии",
  none: "пары не нашлось",
}

export function matchLabel(match: string | null | undefined) {
  return match ? (MATCH_LABEL[match] ?? match) : EMPTY
}

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null
  const numeric = typeof value === "number" ? value : Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

/**
 * Δ со знаком: `+10.00`, `−0.50`. Минус — типографский: в моноширинной
 * колонке дефис короче цифры, и отрицательные строки съезжали бы на полсимвола.
 */
export function formatDelta(value: string | number | null | undefined) {
  const numeric = toNumber(value)
  if (numeric === null) return EMPTY
  const fixed = Math.abs(numeric).toFixed(2)
  if (numeric > 0) return `+${fixed}`
  if (numeric < 0) return `−${fixed}`
  return fixed
}

/** |Δ| без знака — медиана и p90 считаются по модулю. */
export function formatAbs(value: string | number | null | undefined) {
  const numeric = toNumber(value)
  return numeric === null ? EMPTY : Math.abs(numeric).toFixed(2)
}

/** Дата и время одной строкой, в поясе браузера — как везде в консоли. */
export function formatMoment(value: string | null | undefined) {
  if (!value) return EMPTY
  const time = formatTime(value)
  return time ? `${formatDateFull(value)} ${time}` : formatDateFull(value)
}

function RuleChip({ rule, tone }: { rule: LiveRuleRef; tone: "match" | "ours" | "seon" }) {
  return (
    <li
      className={cn(
        "inline-flex max-w-full items-baseline gap-1.5 rounded-sm border px-1.5 py-0.5",
        tone === "match" && "border-line bg-surface-sunken",
        tone === "ours" && "border-accent/35 bg-accent-soft",
        tone === "seon" && "border-review/35 bg-review/10",
      )}
      title={rule.name ?? undefined}
    >
      <span className="shrink-0 text-xs text-fg">{rule.seon_id}</span>
      {rule.blacklist ? (
        <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.04em] text-decline">
          чс
        </span>
      ) : (
        rule.name && <span className="truncate text-xs text-fg-muted">{rule.name}</span>
      )}
    </li>
  )
}

function RuleGroup({
  title,
  hint,
  rules,
  tone,
}: {
  title: string
  hint: string
  rules: LiveRuleRef[]
  tone: "match" | "ours" | "seon"
}) {
  return (
    <div className="min-w-0">
      <p className="flex items-baseline gap-2">
        <span className="stencil-label text-fg">{title}</span>
        <span className="text-xs text-fg-subtle">{formatInt(rules.length)}</span>
      </p>
      <p className="mt-0.5 text-xs leading-snug text-fg-subtle">{hint}</p>
      {rules.length === 0 ? (
        <p className="mt-1.5 text-xs text-fg-subtle">нет</p>
      ) : (
        <ul className="mt-1.5 flex flex-wrap gap-1">
          {rules.map((rule) => (
            <RuleChip key={rule.seon_id} rule={rule} tone={tone} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Разбор правил пары: совпали / только у нас / только у SEON.
 *
 * «Только у SEON» — честно с оговоркой: двойник не записывает, какие правила
 * он не смог проверить из‑за нехватки данных, и такие правила тоже попадают
 * сюда. Сказать «мы его пропустили» про них нельзя — мы его не видели.
 */
export function RuleDiffView({
  rules,
  stacked = false,
}: {
  rules: LiveRuleDiff | null
  /** Столбиком — в узкой панели карточки три колонки чипов не помещаются. */
  stacked?: boolean
}) {
  if (rules === null) return null
  if (!rules.comparable) {
    return (
      <p className="flex items-start gap-2 text-xs leading-relaxed text-review">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>
          Список сработавших правил одной из сторон не разобрался — сверять правила не по чему.
          Баллы и решения выше от этого не зависят.
        </span>
      </p>
    )
  }
  return (
    <div className={cn("grid gap-3", !stacked && "sm:grid-cols-3")}>
      <RuleGroup title="Совпали" hint="сработали у обеих сторон" rules={rules.matched} tone="match" />
      <RuleGroup
        title="Только у нас"
        hint="сработали у двойника, у SEON — нет"
        rules={rules.extra}
        tone="ours"
      />
      <RuleGroup
        title="Только у SEON"
        hint="у нас не сработали или не проверялись: пропуски по данным двойник не пишет"
        rules={rules.missed}
        tone="seon"
      />
    </div>
  )
}

/**
 * Ручная проверка в SEON: статус и метка из `status_update` / `label_update`.
 * Событий нет — так и сказано: пустой статус не значит «одобрено».
 */
export function ReviewView({ review }: { review: LiveSeonReview | null }) {
  if (review === null) {
    return (
      <p className="text-xs leading-relaxed text-fg-subtle">
        Ручной проверки в SEON не было: ни статуса, ни метки не приходило.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
      <span className="flex items-center gap-1.5">
        <span className="stencil-label text-fg-subtle">Статус</span>
        <DecisionBadge state={review.state} />
      </span>
      <span className="flex items-center gap-1.5">
        <span className="stencil-label text-fg-subtle">Метка</span>
        {review.label ? (
          <Badge tone="accent">{review.label}</Badge>
        ) : review.label_removed ? (
          <span className="text-fg-muted">снята</span>
        ) : (
          <span className="text-fg-subtle">не ставили</span>
        )}
      </span>
      <span className="text-fg-subtle">
        {formatMoment(review.updated_at)} · событий {formatInt(review.events)}
      </span>
    </div>
  )
}
